/**
 * The browser half of the platform adapter.
 *
 * Everything in this file is here because it does not exist in React Native.
 * Nothing else should be — if a thing works in both runtimes it belongs in
 * `@fieldvision/core`, where both clients can reach it.
 */
/**
 * A GPS fix, or null.
 *
 * Never throws and never blocks longer than the timeout. FR-03 and FR-08 both
 * proceed without a fix — permission denied and an indoor position that never
 * resolves are ordinary in traditional-trade retail, and a check-in that waited
 * for GPS would be unusable in exactly the outlets that matter most.
 *
 * The timer is belt-and-braces over `getCurrentPosition`'s own `timeout`
 * option: some browsers do not honour it when permission has been granted but
 * no fix is obtainable, which is precisely the indoor case.
 */
async function currentPosition(timeoutMs) {
    if (!('geolocation' in navigator))
        return null;
    return new Promise((resolve) => {
        const settle = (value) => resolve(value);
        const timer = setTimeout(() => settle(null), timeoutMs);
        navigator.geolocation.getCurrentPosition((position) => {
            clearTimeout(timer);
            settle({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                accuracyM: position.coords.accuracy ?? null,
            });
        }, () => {
            clearTimeout(timer);
            settle(null);
        }, { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30_000 });
    });
}
/**
 * Where the auth store notes whether this browser holds a refresh cookie.
 *
 * `'1'` / `'0'` rather than a JSON boolean: the value is read on every cold
 * load, before anything renders, and there is nothing here worth a parser.
 */
const SESSION_HINT_KEY = 'fv-session-hint';
/**
 * The note that spares the login screen a 401 — see `sessionHint` on the
 * adapter for the contract. The Expo app's web adapter carries an identical
 * copy, for the same boundary reason the rest of that file does.
 *
 * localStorage, and that is NOT a contradiction of the sessionStorage choice
 * below: the cookie this describes already outlives the tab, so the note about
 * it must too, and nothing in it can start a session. Every access is guarded
 * because a browser with site data blocked throws on the accessor itself, and
 * a note that cannot be kept has to degrade to "ask the server" — which is what
 * `null` means to the store.
 */
const sessionHint = {
    read: () => {
        try {
            const value = localStorage.getItem(SESSION_HINT_KEY);
            return value === null ? null : value === '1';
        }
        catch {
            return null;
        }
    },
    write: (hasSession) => {
        try {
            localStorage.setItem(SESSION_HINT_KEY, hasSession ? '1' : '0');
        }
        catch {
            // Nothing to do: the next load asks the server, as it always did.
        }
    },
};
export const webPlatform = {
    /*
     * sessionStorage, not localStorage: a shared field device must not carry a
     * working session into the next person's tab, and closing the browser should
     * end the session the way users already expect.
     */
    storage: {
        getItem: (name) => sessionStorage.getItem(name),
        setItem: (name, value) => sessionStorage.setItem(name, value),
        removeItem: (name) => sessionStorage.removeItem(name),
    },
    randomUUID: () => crypto.randomUUID(),
    currentPosition,
    /*
     * The refresh token is an HttpOnly cookie the page cannot read, which is the
     * point — the browser has to be told to send it on every call, or the refresh
     * path silently stops working (ADR-0005).
     */
    credentials: 'include',
    sessionHint,
};
//# sourceMappingURL=platform.web.js.map